import { useMemo } from 'react';

import { getMinimumFeatureGroupForFeature } from '@hotjar/webapp/domains/common/feature';

import {
  planComparisonFeatureGateDetails,
  planComparisonFeatureGatesByReferrer,
} from '../constants';
import type { PlanComparisonFeatureGateDetails, PlanComparisonReferrer } from '../types';

/**
 * Retrieves feature gate details by referrer. Feature gates are manifested in:
 *    1) names/keys as defined in `PlanComparisonFeatureGateDetails`
 *    2) details (like title, description, etc) as defined in `planComparisonFeatureGateDetails`, which is an object
 *        of such details indexed by feature gate keys from `PlanComparisonFeatureGateDetails`
 *
 * However, when application code triggers an upsell, they don't specify which feature gate to show,
 * they just pass a referrer string that identifies the upsell scenario. We need a way to figure out which feature
 * gate to show for certain referrers.
 *
 * To match these referrers to feature gates, we needed to create a map between the two, which you can find in
 * `planComparisonFeatureGatesByReferrer`. It's an object of feature gate keys indexed by referrers.
 *
 * This `usePlanComparisonFeatureGate` hook is meant to abstract away this flow. You can pass it a referrer,
 * and you get back the feature gate details.
 *
 * Not every referrer refers to a feature gate though, so you should absolutely expect and handle this hook
 * returning `null`.
 */
export const usePlanComparisonFeatureGate = (
  referrer: PlanComparisonReferrer,
): PlanComparisonFeatureGateDetails | null => {
  return useMemo(() => {
    const featureGate = planComparisonFeatureGatesByReferrer[referrer] ?? null;

    /**
     * Couldn't resolve a feature gate by referrer. This isn't necessarily an error,
     * but rather a case when the referrer doesn't refer to a supported feature gate.
     */
    if (!featureGate) {
      return null;
    }

    const details = planComparisonFeatureGateDetails[featureGate];

    /**
     * If the minimum feature group is not specified, but the feature name is, let's try get
     * the minimum feature group by feature name.
     */
    if (!details.minimumFeatureGroup && details.featureName) {
      details.minimumFeatureGroup = getMinimumFeatureGroupForFeature(details.featureName);
    }

    return details;
  }, [referrer]);
};
